// Cliente del KRAI LOCAL AGENT (puente local seguro que corre en el Mac del usuario).
// El agente escucha SOLO en localhost/127.0.0.1. El navegador habla directamente con él.
//
// Reto técnico: cuando la app se ejecuta en HTTPS (preview/publicada) y contacta con
// http://127.0.0.1:8765, Chrome aplica "Private Network Access / Local Network Access".
// Una petición bloqueada por política del navegador produce el MISMO error opaco que
// "servidor apagado", así que aquí clasificamos la causa real con la mayor precisión
// posible (refused / blocked_policy / blocked_cors / http_error / timeout / ok).

export const AGENT_URL = (process.env.REACT_APP_AGENT_URL || "http://127.0.0.1:8765").replace(/\/+$/, "");

const DEV = process.env.NODE_ENV !== "production";
function dlog(...args) {
  if (DEV) console.log("%c[KRAI-AGENT]", "color:#34d399", ...args);
}

export class AgentHttpError extends Error {
  constructor(status, body) {
    super(`HTTP ${status}`);
    this.name = "AgentHttpError";
    this.status = status;
    this.body = body;
  }
}

// --- Detección de entorno ---------------------------------------------------
export function getEnvironment() {
  const { protocol, hostname, origin } = window.location;
  const isLocalhost = ["localhost", "127.0.0.1", "[::1]", "::1"].includes(hostname);
  const isHttps = protocol === "https:";
  const agentIsLoopback = /^https?:\/\/(127\.0\.0\.1|localhost|\[::1\])(:\d+)?/i.test(AGENT_URL);
  return {
    origin,
    isLocalhost,
    isHttps,
    agentIsLoopback,
    // "local" = servido desde localhost:3000 ; "published" = preview o publicada (HTTPS)
    mode: isLocalhost ? "local" : "published",
    // https (público) -> http loopback: territorio de Private Network Access
    crossScheme: isHttps && !isLocalhost && agentIsLoopback,
    agentUrl: AGENT_URL,
  };
}

// --- fetch con timeout ------------------------------------------------------
const LOOPBACK_RE = /^https?:\/\/(127\.\d+\.\d+\.\d+|localhost|\[::1\])(:\d+)?\//i;

async function fetchWithTimeout(url, opts = {}, timeoutMs = 6000) {
  const ctrl = new AbortController();
  const id = setTimeout(() => ctrl.abort(), timeoutMs);
  if (opts.signal) opts.signal.addEventListener("abort", () => ctrl.abort(), { once: true });
  const lna = LOOPBACK_RE.test(url) ? { targetAddressSpace: "loopback" } : {};
  try {
    return await fetch(url, { ...opts, ...lna, signal: ctrl.signal });
  } finally {
    clearTimeout(id);
  }
}

async function safeText(res) {
  try {
    return await res.text();
  } catch {
    return "";
  }
}

// --- Diagnóstico de la conexión con el agente -------------------------------
// Devuelve: { reachable, code, httpStatus?, env }
//   code: "ok" | "http_error" | "blocked_policy" | "blocked_cors" | "refused" | "timeout"
export async function diagnoseAgent() {
  const env = getEnvironment();
  const url = `${AGENT_URL}/agent/ping`;
  dlog("diagnose ->", url, "| entorno:", env.mode, "| origin:", env.origin);

  // 1) Petición CORS real (lo que la app necesita de verdad para leer JSON).
  try {
    const res = await fetchWithTimeout(url, { method: "GET", mode: "cors", cache: "no-store" }, 6000);
    if (res.ok) {
      dlog("ping OK (cors) HTTP", res.status);
      return { reachable: true, code: "ok", httpStatus: res.status, env };
    }
    dlog("ping devolvió HTTP", res.status, "(error HTTP real del agente)");
    return { reachable: true, code: "http_error", httpStatus: res.status, env };
  } catch (err) {
    const aborted = err && err.name === "AbortError";
    dlog("ping CORS falló:", err && err.name, err && err.message, aborted ? "(timeout)" : "");

    // 2) Sonda opaca (no-cors): si el servidor responde, la promesa RESUELVE aunque
    //    no podamos leer el cuerpo. Nos permite distinguir "apagado" de "bloqueado".
    let opaqueOk = false;
    try {
      await fetchWithTimeout(url, { method: "GET", mode: "no-cors", cache: "no-store" }, 5000);
      opaqueOk = true;
    } catch (err2) {
      dlog("sonda no-cors falló:", err2 && err2.name, err2 && err2.message);
    }

    if (opaqueOk) {
      // Algo está escuchando y respondió, pero el navegador bloqueó la lectura.
      const code = env.crossScheme ? "blocked_policy" : "blocked_cors";
      dlog("clasificación:", code, "(el agente responde pero el navegador bloquea)");
      return { reachable: false, code, env };
    }
    if (aborted) {
      dlog("clasificación: timeout");
      return { reachable: false, code: "timeout", env };
    }
    // En HTTPS público, un fallo total puede seguir siendo PNA (que también bloquea no-cors).
    if (env.crossScheme) {
      dlog("clasificación: blocked_policy (https->loopback, posible PNA que bloquea incluso no-cors)");
      return { reachable: false, code: "blocked_policy", env };
    }
    dlog("clasificación: refused (nada escuchando en", AGENT_URL, ")");
    return { reachable: false, code: "refused", env };
  }
}

// --- Llamadas reales al agente (requieren CORS legible) ---------------------
async function getJson(path, timeoutMs = 15000) {
  const res = await fetchWithTimeout(`${AGENT_URL}${path}`, { method: "GET", mode: "cors", cache: "no-store" }, timeoutMs);
  if (!res.ok) throw new AgentHttpError(res.status, await safeText(res));
  return res.json();
}

async function postJson(path, timeoutMs = 190000) {
  const res = await fetchWithTimeout(
    `${AGENT_URL}${path}`,
    { method: "POST", mode: "cors", headers: { Accept: "application/json" } },
    timeoutMs
  );
  if (!res.ok) throw new AgentHttpError(res.status, await safeText(res));
  return res.json();
}

export async function pingAgent() {
  return getJson("/agent/ping", 6000);
}

export async function getAgentStatus() {
  return getJson("/agent/status", 15000);
}

export async function getAgentPlusVersion() {
  return getJson("/agent/plus/version", 6000);
}

export async function getJobProgress() {
  return getJson("/agent/jobs/progress", 6000);
}

export async function cancelJob() {
  const res = await fetchWithTimeout(AGENT_URL + "/agent/jobs/cancel", {
    method: "POST", mode: "cors", headers: { Accept: "application/json" },
  }, 15000);
  if (!res.ok) throw new AgentHttpError(res.status, await safeText(res));
  return res.json();
}

export async function getInstallProgress() {
  return getJson("/agent/models/install/progress", 8000);
}

export async function getPendingInstalls() {
  return getJson("/agent/models/pending", 15000);
}

export async function discardPendingInstall(repo_id) {
  const res = await fetchWithTimeout(AGENT_URL + "/agent/models/pending/discard", {
    method: "POST", mode: "cors", headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ repo_id }),
  }, 60000);
  if (!res.ok) throw new AgentHttpError(res.status, await safeText(res));
  return res.json();
}

const hfSizeCache = {};
export async function getHfRepoSize(repoId) {
  if (hfSizeCache[repoId] !== undefined) return hfSizeCache[repoId];
  const res = await fetch(`https://huggingface.co/api/models/${repoId}/tree/main?recursive=true`);
  if (!res.ok) return null;
  const items = await res.json();
  hfSizeCache[repoId] = items.reduce((s, x) => s + (x.type === "file" ? x.size || 0 : 0), 0);
  return hfSizeCache[repoId];
}

export async function agentConnect() {
  return postJson("/agent/connect", 190000);
}

export async function agentDisconnect() {
  return postJson("/agent/disconnect", 60000);
}

// Puente Chat/Vision (solo preview/publicada): el navegador -> agente -> llama-server.
export async function agentChat(payload) {
  const res = await fetchWithTimeout(
    `${AGENT_URL}/agent/chat`,
    {
      method: "POST",
      mode: "cors",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(payload),
    },
    190000
  );
  if (!res.ok) throw new AgentHttpError(res.status, await safeText(res));
  return res.json();
}

export async function agentVision(payload) {
  const res = await fetchWithTimeout(
    `${AGENT_URL}/agent/vision`,
    {
      method: "POST",
      mode: "cors",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(payload),
    },
    190000
  );
  if (!res.ok) throw new AgentHttpError(res.status, await safeText(res));
  return res.json();
}


export async function agentImage(payload) {
  const res = await fetchWithTimeout(
    `${AGENT_URL}/agent/image`,
    {
      method: "POST",
      mode: "cors",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(payload),
    },
    310000
  );
  if (!res.ok) throw new AgentHttpError(res.status, await safeText(res));
  return res.json();
}


export async function agentImageEdit(payload) {
  try {
    const res = await fetchWithTimeout(
      `${AGENT_URL}/agent/image-edit`,
      {
        method: "POST",
        mode: "cors",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(payload),
      },
      // El agente puede calcular hasta 40 minutos (Qwen en Macs de 16 GB); deja margen.
      2460000
    );
    if (!res.ok) throw new AgentHttpError(res.status, await safeText(res));
    return res.json();
  } catch (error) {
    if (error?.name === "AbortError" || error?.name === "TimeoutError") {
      throw new Error("Se agotó la espera de 41 minutos. Comprueba el estado del agente antes de iniciar otra edición. Qwen consume mucha memoria en este Mac; SenseNova completó la prueba local.");
    }
    throw error;
  }
}


export async function agentVideo(payload) {
  try {
    const res = await fetchWithTimeout(
      `${AGENT_URL}/agent/video`,
      {
        method: "POST",
        mode: "cors",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(payload),
      },
      960000
    );
    if (!res.ok) throw new AgentHttpError(res.status, await safeText(res));
    return res.json();
  } catch (error) {
    if (error?.name === "AbortError" || error?.name === "TimeoutError") {
      throw new Error("Se agotó la espera del vídeo. Comprueba el estado del agente antes de repetir la generación o reduce el número de fotogramas.");
    }
    throw error;
  }
}


export async function getAgentModels() {
  const res = await fetchWithTimeout(AGENT_URL + "/agent/models", { method: "GET", mode: "cors", cache: "no-store" }, 15000);
  if (!res.ok) throw new AgentHttpError(res.status, await safeText(res));
  return res.json();
}

export async function installAgentModel(payload, signal) {
  const res = await fetchWithTimeout(AGENT_URL + "/agent/models/install", {
    method: "POST", mode: "cors", headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(payload), signal,
  }, 172800000);
  if (!res.ok) throw new AgentHttpError(res.status, await safeText(res));
  return res.json();
}

// Contrato para krai_agent.py: POST /agent/models/install/cancel body { repo_id, name }
//   -> mata la descarga en curso y borra la carpeta parcial -> { ok, message? } | { ok:false, error }
export async function cancelAgentInstall(payload) {
  const res = await fetchWithTimeout(AGENT_URL + "/agent/models/install/cancel", {
    method: "POST", mode: "cors", headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(payload),
  }, 20000);
  if (!res.ok) throw new AgentHttpError(res.status, await safeText(res));
  return res.json();
}

export async function stopInstall(abortCtrl, payload) {
  abortCtrl?.abort();
  return cancelAgentInstall(payload).catch((e) => ({
    ok: false,
    error: e?.status === 404 || e?.status === 405
      ? "Tu Agent aún no tiene /agent/models/install/cancel: la descarga puede seguir en el Mac."
      : e?.message || "No se pudo avisar al Agent",
  }));
}

// Contrato para krai_agent.py: POST /agent/pick-folder
//   abre el selector nativo de macOS (osascript 'choose folder') -> { ok, path } | { ok:false, cancelled:true }
export async function pickFolder() {
  const res = await fetchWithTimeout(AGENT_URL + "/agent/pick-folder", {
    method: "POST", mode: "cors", headers: { Accept: "application/json" },
  }, 300000);
  if (!res.ok) throw new AgentHttpError(res.status, await safeText(res));
  return res.json();
}

export async function removeAgentModel(modelId) {
  const res = await fetchWithTimeout(AGENT_URL + "/agent/models/" + encodeURIComponent(modelId), {
    method: "DELETE", mode: "cors", headers: { Accept: "application/json" },
  }, 60000);
  if (!res.ok) throw new AgentHttpError(res.status, await safeText(res));
  return res.json();
}

// --- Carpeta de descarga de modelos (gestionada por el Agent en el Mac) ---
// Contrato que el Agent (krai_agent.py) debe implementar:
//   GET  /agent/models/dir   -> { ok, path, default_path, exists, writable, free_gb?, total_gb? }
//   POST /agent/models/dir   body: { path }
//        valida que la ruta exista y sea escribible, la persiste y la usa en _model_root.
//        -> { ok, path, exists, writable, free_gb?, total_gb? } | { ok:false, error }
export async function getModelsDir() {
  const res = await fetchWithTimeout(AGENT_URL + "/agent/models/dir", { method: "GET", mode: "cors", cache: "no-store" }, 15000);
  if (!res.ok) throw new AgentHttpError(res.status, await safeText(res));
  return res.json();
}

export async function setModelsDir(path) {
  const res = await fetchWithTimeout(AGENT_URL + "/agent/models/dir", {
    method: "POST", mode: "cors", headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ path }),
  }, 20000);
  if (!res.ok) throw new AgentHttpError(res.status, await safeText(res));
  return res.json();
}
